import { useEffect, useRef } from 'react';
import { useAuthSessionInternal } from './auth-provider';
import type { AuthEvent } from '../types/events';

/**
 * Subscribes to a domain auth event for the lifetime of the component.
 *
 * The handler is stable-ref wrapped, so you do not need to memoize it.
 * The subscription is set up once on mount and torn down on unmount.
 *
 * @example — GTM sign-up tracking
 * useAuthEvent('sign_up_complete', ({ user, newsletterConsent }) => {
 *   AnalyticsController.trackEvent(GTM_AUTH_SIGN_UP_GENERAL);
 * });
 *
 * @example — GTM social sign-up tracking
 * useAuthEvent('social_phone_collection_complete', ({ provider }) => {
 *   AnalyticsController.trackEvent(GTM_AUTH_SIGN_UP_GENERAL);
 * });
 */
export function useAuthEvent<K extends AuthEvent['type']>(
  type: K,
  handler: (event: Extract<AuthEvent, { type: K }>) => void,
): void {
  const session = useAuthSessionInternal();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    return session.internals.eventBus.on(type, event => handlerRef.current(event));
  }, [session, type]);
}
